"use client";

import Image from "next/image";
import Link from "next/link";
import { useState, useEffect, useRef } from "react";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { useQuery, useMutation } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Id } from "@/convex/_generated/dataModel";
import { toast } from "@/lib/toast";
import StoryCard, { storyFrameFor } from "./stories/StoryCard";
import { toStoryView } from "./stories/toStoryView";
import VideoPlayerView from "./stories/VideoPlayerView";
import { getOrCreateGuestId, getGuestAvatar, getGuestName } from "./stories/utils/videoUtils";
import { ScanFace, X } from "lucide-react";
import TrackingChatDialog from "@/components/tracking/TrackingChatDialog";
import GamesTabContent from "./GamesTabContent";
import ReviewsTabContent from "./reviews/ReviewsTabContent";

interface BrandData {
  _id?: string;
  name: string;
  tagline?: string;
  description?: string;
  location?: string;
  yearsActive?: string;
  shopNumber?: string;
  profileImage?: string;
  coverImages?: string[];
  socialLinks?: {
    website?: string;
    instagram?: string;
    twitter?: string;
    facebook?: string;
    tiktok?: string;
    whatsapp?: string;
    youtube?: string;
  };
  stats?: {
    totalProducts?: string;
    followers?: string;
    avgRating?: string;
  };
}

interface BrandsHeroSectionProps {
  brandData?: BrandData;
  productCount?: number;
  onTabChange?: (tab: string) => void;
  activeTab?: string;
  domain?: string;
  brandId?: string; // Convex brand ID
  // Edit mode props
  isEditMode?: boolean;
  onEditCoverImages?: () => void;
  onEditProfileImage?: () => void;
  uploadingProfile?: boolean;
  uploadingCover?: boolean;
  /** Show the Services tab (brand has bookable service products). */
  hasServices?: boolean;
  /** The owner shows services before products on the website: the Services tab comes before Products. */
  servicesFirst?: boolean;
  /** Show the Rooms tab (accommodation brand with room types on sale). */
  hasRooms?: boolean;
  /**
   * How many of the shop's collections the website shows today (Sep 2026):
   * the Collections figure in the header, and a Collections tab when > 0.
   */
  collectionCount?: number;
}

type TabType = "Products" | "Collections" | "Rooms" | "Services" | "Stories" | "Reviews" | "Games";

/** Tailwind needs whole class names in the source (one per number of figures). */
const STAT_COLS: Record<number, string> = { 1: "md:grid-cols-1", 2: "md:grid-cols-2", 3: "md:grid-cols-3", 4: "md:grid-cols-4" };

export default function BrandsHeroSection({
  brandData,
  productCount,
  onTabChange,
  activeTab: externalActiveTab,
  domain,
  brandId,
  isEditMode = false,
  onEditCoverImages,
  onEditProfileImage,
  uploadingProfile = false,
  uploadingCover = false,
  hasServices = false,
  servicesFirst = false,
  hasRooms = false,
  collectionCount = 0,
}: BrandsHeroSectionProps) {
  const [activeSlide, setActiveSlide] = useState(0);
  const [thumbPage, setThumbPage] = useState(0);
  const [isHoveringThumbs, setIsHoveringThumbs] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>("Products");
  const [isLoadingFollow, setIsLoadingFollow] = useState(false);
  const [selectedStory, setSelectedStory] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Games modal state
  const [isGamesModalOpen, setIsGamesModalOpen] = useState(false);
  const [selectedGame, setSelectedGame] = useState<any>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);

  // Guard flag to prevent modal from reopening during close operation
  const isClosingRef = useRef(false);

  // URL management for deep linking
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isModalOpen || isGamesModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen, isGamesModalOpen]);

  // Get current user to check if they're the brand owner
  const currentUser = useQuery(api.auth.getCurrentAppUser) as any;

  // Query follower count - use null check to avoid TypeScript error
  const followerCount = useQuery(
    api.brands.social.getFollowerCount,
    brandId ? { brandId: brandId as any } : "skip"
  );

  // Query if current user is following
  const isFollowingBrand = useQuery(
    api.brands.social.isFollowing,
    brandId ? { brandId: brandId as any } : "skip"
  );

  // The shop's real star rating (approved reviews) — shown only once it has some.
  const rating = useQuery(
    api.reviews.queries.getBrandRatingSummary,
    brandId ? { brandId: brandId as any } : "skip"
  ) as { count: number; average: number } | undefined;

  // Query products for preview (only in edit mode)
  const productsData = useQuery(
    api.products.shop.getShopProducts,
    brandId && isEditMode ? {
      brandId: brandId as any,
      limit: 4
    } : "skip"
  );

  // Query stories for Stories tab
  const storiesData = useQuery(
    api.stories.core.getStories,
    brandId ? { brandId: brandId as any } : "skip"
  );

  // The Games tab only when the shop runs a game, live or starting soon
  // (Frank, 28 Sep 2026). A shared game link (?game=) still opens it.
  const hasGames =
    useQuery(api.games.v2.shop.hasGames, brandId ? { brandId: brandId as any } : "skip") === true;

  // The tabs scroll sideways on a phone. The browser's own bar was a thick
  // grey line across the whole width (Frank: "ugly"): it's hidden, and a
  // short indicator under the tabs shows where you are, only when they
  // don't all fit.
  const tabsRef = useRef<HTMLDivElement>(null);
  const [tabsScroll, setTabsScroll] = useState({ overflow: false, progress: 0, shown: 1 });
  useEffect(() => {
    const el = tabsRef.current;
    if (!el) return;
    const measure = () => {
      const max = el.scrollWidth - el.clientWidth;
      const next = {
        overflow: max > 4,
        progress: max > 0 ? Math.min(1, Math.max(0, el.scrollLeft / max)) : 0,
        shown: el.scrollWidth > 0 ? el.clientWidth / el.scrollWidth : 1,
      };
      setTabsScroll((prev) =>
        prev.overflow === next.overflow && Math.abs(prev.progress - next.progress) < 0.01 && Math.abs(prev.shown - next.shown) < 0.01 ? prev : next,
      );
    };
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    observer?.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      el.removeEventListener("scroll", measure);
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [hasGames, hasRooms, hasServices, collectionCount, storiesData?.length]);

  // Skin Analysis, when the brand runs it (Korean Beauty, 2026-09-24): a
  // button beside Message opens the shop's own /skin page, so a shopper finds
  // it without being sent a link. (A tab beside Products was dropped — Frank:
  // "the button is enough".)
  const skinAnalysis = useQuery(
    api.skin.sessions.isEnabled,
    brandId ? { brandId: brandId as any } : "skip"
  ) as { enabled: boolean; themeColor?: string } | undefined;
  const skinAccent = skinAnalysis?.themeColor || "#A05C4A";

  // Query comments for selected story
  const commentsData = useQuery(
    api.stories.comments.getComments,
    selectedStory?._id ? { storyId: selectedStory._id } : "skip"
  );

  // Check if current user is the brand owner
  const isBrandOwner = currentUser && brandId && currentUser.brandIds?.includes(brandId as any);

  // Mutations
  const followBrand = useMutation(api.brands.social.followBrand);
  const unfollowBrand = useMutation(api.brands.social.unfollowBrand);
  const addComment = useMutation(api.stories.comments.addComment);
  const incrementViews = useMutation(api.stories.core.incrementViews);

  // Handle follow/unfollow
  const handleFollowToggle = async () => {
    if (!brandId) {
      toast.error("Brand ID not available");
      return;
    }

    // Following needs an account. Ask BEFORE the round trip — a shopper who is
    // not signed in used to press Follow and get a red "Something went wrong",
    // which told them nothing (Korean Beauty reported it).
    if (currentUser === null) {
      toast.error("Sign in to follow this shop.", {
        action: { label: "Sign in", onClick: () => { window.location.href = "/signin"; } },
      });
      return;
    }
    if (currentUser === undefined) return; // still loading — don't guess

    setIsLoadingFollow(true);
    try {
      if (isFollowingBrand) {
        await unfollowBrand({ brandId: brandId as any });
        toast.success("Unfollowed successfully");
      } else {
        await followBrand({ brandId: brandId as any });
        toast.success("Following brand");
      }
    } catch (error) {
      // Log the full error for debugging
      console.error("Follow error:", error);

      // Handle user-friendly error messages from Convex
      // Convex errors can have the message in error.message or error.data?.message
      // A ConvexError carries its message in `data` — read that FIRST, or the
      // real sentence is lost behind Convex's redacted wrapper.
      let errorMessage = "Something went wrong";

      const convexError = error as any;
      if (typeof convexError?.data === "string") {
        errorMessage = convexError.data;
      } else if (typeof convexError?.data?.message === "string") {
        errorMessage = convexError.data.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === "object" && error !== null) {
        errorMessage = convexError.message || errorMessage;
      }

      // Extract the actual error message if it's wrapped in Convex error format
      // Pattern: "[CONVEX M(path)] [Request ID: xxx] Actual Error Message"
      const match = errorMessage.match(/\[Request ID: [^\]]+\]\s*(.+)$/);
      if (match && match[1]) {
        errorMessage = match[1].trim();
      }

      // Show user-friendly error
      if (errorMessage.includes("sign in") || errorMessage.includes("Sign in")) {
        toast.error(errorMessage, {
          action: {
            label: "Sign In",
            onClick: () => window.location.href = "/signin",
          },
        });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsLoadingFollow(false);
    }
  };

  // Handle story click - open modal and increment views
  const handleStoryClick = async (story: any) => {
    console.log("handleStoryClick called with story:", story);

    // Handle both id and _id formats
    const storyId = story._id || story.id;

    if (!storyId) {
      console.error("Story is missing id:", story);
      return;
    }

    // Find the original story from storiesData to get all fields
    const originalStory = storiesData?.find((s: any) => s._id === storyId);
    if (!originalStory) {
      console.error("Could not find original story:", storyId);
      return;
    }

    setSelectedStory(originalStory);
    setIsModalOpen(true);

    // Update URL with video parameter
    router.push(`${pathname}?video=${storyId}`, { scroll: false });

    // Increment view count
    try {
      await incrementViews({ storyId });
    } catch (error) {
      console.error("Failed to increment views:", error);
    }
  };

  // Handle close modal
  const handleCloseModal = () => {
    // Set guard flag to prevent re-opening during close operation
    isClosingRef.current = true;

    setIsModalOpen(false);
    setSelectedStory(null);

    // Remove video parameter from URL
    router.push(pathname, { scroll: false });

    // Reset guard flag after navigation completes
    setTimeout(() => {
      isClosingRef.current = false;
    }, 100);
  };

  // Handle add comment
  const handleAddComment = async (storyId: string, text: string) => {
    console.log("handleAddComment called with:", { storyId, text, type: typeof storyId });

    if (!storyId || storyId === "undefined" || storyId === "null") {
      console.error("Story ID is invalid:", storyId);
      toast.error("Story ID is missing");
      return;
    }

    try {
      // Get or create guest ID
      const guestId = getOrCreateGuestId();
      const guestName = getGuestName(guestId);
      const guestAvatar = getGuestAvatar(guestId);

      const commentData = {
        storyId: storyId as Id<"stories">,
        text: text.trim(),
        guestId,
        guestName,
        guestAvatar,
      };

      console.log("Calling addComment with:", commentData);

      await addComment(commentData);

      toast.success("Comment added!");
    } catch (error) {
      console.error("Failed to add comment:", error);
      toast.error("Failed to add comment");
    }
  };

  // Auto-open modal if video parameter exists in URL
  useEffect(() => {
    const videoId = searchParams.get('video');
    const gameId = searchParams.get('game');

    // Handle video deep linking
    if (videoId && storiesData && !isModalOpen && !isClosingRef.current) {
      const story = storiesData.find((s: any) => s._id === videoId);

      if (story) {
        console.log("Auto-opening video from URL:", videoId);
        setSelectedStory(story);
        setIsModalOpen(true);

        // Increment view count
        incrementViews({ storyId: videoId as any }).catch(error => {
          console.error("Failed to increment views:", error);
        });
      } else {
        console.error("Video not found:", videoId);
        toast.error("Video not found");
        // Remove invalid video parameter
        router.push(pathname, { scroll: false });
      }
    }

    // Handle game deep linking
    if (gameId && !isGamesModalOpen && !isClosingRef.current) {
      console.log("Auto-opening game from URL:", gameId);
      setIsGamesModalOpen(true);
      // The GamesModalView will handle loading the specific game
    }
  }, [searchParams, storiesData, isModalOpen, isGamesModalOpen, pathname, router, incrementViews]);

  // Sync with external activeTab if provided
  useEffect(() => {
    if (externalActiveTab) {
      setActiveTab(externalActiveTab as TabType);
    }
  }, [externalActiveTab]);

  // Notify parent of initial active tab on mount
  useEffect(() => {
    // Only notify if no external tab is provided
    if (!externalActiveTab && onTabChange) {
      onTabChange("Products");
    }
  }, [externalActiveTab, onTabChange]);

  // Get cover images from database or use empty array
  const coverImages = brandData?.coverImages || [];
  const hasCoverImages = coverImages.length > 0;

  // Reset slide when cover images change (e.g. store switch)
  const coverImagesKey = coverImages.join(",");
  useEffect(() => {
    setActiveSlide(0);
    setThumbPage(0);
  }, [coverImagesKey]);

  // Cycle through cover images if available
  useEffect(() => {
    if (coverImages.length > 1 && !isHoveringThumbs) {
      const interval = setInterval(() => {
        setActiveSlide((prev) => {
          const next = (prev + 1) % coverImages.length;
          // Auto-advance thumbnail page if the new active slide is out of view
          if (next >= (thumbPage + 1) * 4 || next < thumbPage * 4) {
            setThumbPage(Math.floor(next / 4));
          }
          return next;
        });
      }, 6000);
      return () => clearInterval(interval);
    } else if (coverImages.length <= 1) {
      setActiveSlide(0);
    }
  }, [coverImages.length, isHoveringThumbs, thumbPage]);

  return (
    <div
      className="w-full overflow-x-hidden"
      style={{
        backgroundColor: '#ffffff',
        color: '#0f172a',
        colorScheme: 'light',
      }}
    >
      {/* Hero Timeline Slider */}
      <div className="relative w-full aspect-video overflow-hidden bg-slate-200" style={{ zIndex: 1 }}>
        {hasCoverImages ? (
          <>
            <div className="absolute inset-0 z-0 pointer-events-none">
              {coverImages.map((image, idx) => (
                <div
                  key={idx}
                  className={`absolute inset-0 transition-opacity duration-[1500ms] ease-in-out pointer-events-none ${idx === activeSlide
                    ? "opacity-100"
                    : "opacity-0"
                    }`}
                >
                  <Image
                    src={image}
                    alt={`${brandData?.name || "Brand"} Cover ${idx + 1}`}
                    fill
                    sizes="100vw"
                    className="object-cover"
                    priority={idx === 0}
                    unoptimized
                  />
                  {/* Subtle Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/30 pointer-events-none"></div>
                </div>
              ))}
            </div>

            {/* Glassmorphism Thumbnail Pagination */}
            {coverImages.length > 1 && (
              <div
                className="absolute bottom-4 right-4 md:bottom-6 md:right-6 flex items-center justify-center p-1.5 md:p-2 rounded-full z-30 transition-all duration-300 cursor-pointer"
                style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.15)',
                  touchAction: 'manipulation',
                }}
                onMouseEnter={() => setIsHoveringThumbs(true)}
                onMouseLeave={() => setIsHoveringThumbs(false)}
              >
                {/* Left Arrow (if needed) */}
                {coverImages.length > 4 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setThumbPage(p => Math.max(0, p - 1));
                    }}
                    disabled={thumbPage === 0}
                    className="cursor-pointer w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-white/80 hover:text-white disabled:opacity-30 disabled:hover:text-white/80 transition-colors mr-0.5 md:mr-1 shrink-0"
                    style={{ touchAction: 'manipulation' }}
                  >
                    <svg className="w-3.5 h-3.5 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" /></svg>
                  </button>
                )}

                <div className="flex gap-2 md:gap-2.5 mx-1 md:mx-1.5 transition-all duration-500">
                  {coverImages.slice(thumbPage * 4, thumbPage * 4 + 4).map((image, idx) => {
                    const actualIdx = thumbPage * 4 + idx;
                    const isActive = actualIdx === activeSlide;
                    return (
                      <button
                        key={actualIdx}
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setActiveSlide(actualIdx);
                        }}
                        onTouchEnd={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setActiveSlide(actualIdx);
                        }}
                        className={`cursor-pointer relative w-10 h-10 md:w-12 md:h-12 shrink-0 rounded-full overflow-hidden transition-all duration-300 select-none ${isActive
                          ? "ring-2 ring-white scale-110 shadow-lg"
                          : "ring-1 ring-white/30 hover:ring-white/70 hover:scale-105 opacity-80 hover:opacity-100"
                          }`}
                        style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                        aria-label={`Go to slide ${actualIdx + 1}`}
                      >
                        <Image
                          src={image}
                          alt={`Thumbnail ${actualIdx + 1}`}
                          fill
                          sizes="(max-width: 768px) 40px, 48px"
                          className="object-cover pointer-events-none select-none"
                          draggable={false}
                          unoptimized
                        />
                      </button>
                    );
                  })}
                </div>

                {/* Right Arrow (if needed) */}
                {coverImages.length > 4 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setThumbPage(p => Math.min(Math.ceil(coverImages.length / 4) - 1, p + 1));
                    }}
                    disabled={thumbPage >= Math.ceil(coverImages.length / 4) - 1}
                    className="cursor-pointer w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center text-white/80 hover:text-white disabled:opacity-30 disabled:hover:text-white/80 transition-colors ml-0.5 md:ml-1 shrink-0"
                    style={{ touchAction: 'manipulation' }}
                  >
                    <svg className="w-3.5 h-3.5 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" /></svg>
                  </button>
                )}
              </div>
            )}

            {/* Edit Button - Only in Edit Mode */}
            {isEditMode && onEditCoverImages && (
              <button
                onClick={onEditCoverImages}
                disabled={uploadingCover}
                className="absolute top-6 right-6 w-12 h-12 rounded-full flex items-center justify-center transition-all shadow-lg hover:scale-110 disabled:opacity-50 z-[100] bg-blue-500"
                title="Edit cover images"
              >
                {uploadingCover ? (
                  <svg className="animate-spin h-6 w-6 text-white" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                )}
              </button>
            )}
          </>
        ) : (
          // Placeholder when no cover images
          <div className="absolute inset-0 bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900">
            <div className="absolute inset-0 bg-[url('/images/pattern-dots.svg')] opacity-10" />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white/60">
              <svg
                className="w-16 h-16 mb-2 opacity-20"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              {/* Show "Add Cover Images" button in edit mode, otherwise "Setup Profile" for brand owners */}
              {isEditMode && onEditCoverImages ? (
                <button
                  onClick={onEditCoverImages}
                  className="mt-4 px-6 py-3 bg-blue-500 text-white rounded-lg font-bold text-sm uppercase tracking-wide hover:bg-blue-600 transition-all shadow-lg"
                >
                  Add Cover Images
                </button>
              ) : isBrandOwner ? (
                <Link
                  href="/pos/store-settings"
                  className="mt-4 px-6 py-3 bg-white text-gray-900 rounded-lg font-bold text-sm uppercase tracking-wide hover:bg-gray-100 transition-all shadow-lg"
                >
                  Setup Profile
                </Link>
              ) : null}
            </div>
          </div>
        )}
      </div>

      {/* Profile Header Wrapper */}
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 relative" style={{ backgroundColor: '#ffffff' }}>
        <div className="flex flex-row items-start gap-3 sm:gap-4 md:gap-8 -mt-10 md:-mt-16 mb-6 sm:mb-8 relative" style={{ zIndex: 10 }}>
          {/* Identity Image (Profile Pic) */}
          <div className="relative flex-shrink-0" style={{ zIndex: 100 }}>
            <div className="w-24 h-24 md:w-36 md:h-36 rounded-full overflow-hidden flex items-center justify-center transition-transform hover:scale-[1.02] duration-300 relative" style={{ zIndex: 100, backgroundColor: '#ffffff', border: '4px solid #ffffff', boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)' }}>
              {uploadingProfile && (
                <div className="absolute inset-0 flex items-center justify-center" style={{ zIndex: 105, backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
                  <svg className="animate-spin h-6 w-6 text-white" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              )}
              {brandData?.profileImage ? (
                <Image
                  src={brandData.profileImage}
                  alt={`${brandData?.name || "Brand"} Profile`}
                  width={144}
                  height={144}
                  className="object-cover w-full h-full"
                  unoptimized
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: '#f1f5f9' }}>
                  <span className="text-3xl md:text-5xl font-black" style={{ color: '#cbd5e1' }}>
                    {brandData?.name?.charAt(0).toUpperCase() || "B"}
                  </span>
                </div>
              )}

              {/* Edit Button - Only in Edit Mode */}
              {isEditMode && onEditProfileImage && (
                <button
                  onClick={onEditProfileImage}
                  disabled={uploadingProfile}
                  className="absolute top-1 right-1 md:top-2 md:right-2 w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center transition-all hover:scale-110 disabled:opacity-50 bg-blue-500"
                  style={{ zIndex: 150, boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)' }}
                  title={brandData?.profileImage ? "Change profile image" : "Upload profile image"}
                >
                  <svg className="w-4 h-4 md:w-5 md:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {brandData?.profileImage ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1M16 8l-4-4m0 0L8 8m4-4v12" />
                    )}
                  </svg>
                </button>
              )}
            </div>
            {/* Online Status Badge */}
            <div className="absolute bottom-2 right-2 w-4 h-4 md:w-5 md:h-5 rounded-full" style={{ zIndex: 105, backgroundColor: '#10b981', border: '2px solid #ffffff', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)' }}></div>
          </div>

          {/* Brand Identity Text */}
          <div className="flex-1 pt-12 md:pt-20 space-y-1 min-w-0" style={{ color: '#0f172a' }}>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <h1 className="text-xl sm:text-2xl md:text-4xl font-black tracking-tight truncate" style={{ color: '#0f172a' }} title={brandData?.name || "Brand Name"}>
                  {brandData?.name || "Brand Name"}
                </h1>
                {/* Verified Badge */}
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-blue-500 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
                </svg>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handleFollowToggle}
                  disabled={isLoadingFollow}
                  className={`px-4 sm:px-5 py-1.5 rounded-full text-xs sm:text-sm font-bold transition-all ${isFollowingBrand
                    ? "border hover:bg-slate-50"
                    : "hover:bg-slate-800"
                    }`}
                  style={{
                    backgroundColor: isFollowingBrand ? '#ffffff' : '#000000',
                    color: isFollowingBrand ? '#0f172a' : '#ffffff',
                    borderColor: isFollowingBrand ? '#e2e8f0' : 'transparent',
                    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                  }}
                >
                  {isLoadingFollow ? "..." : isFollowingBrand ? "Following" : "Follow"}
                </button>
                <button
                  onClick={() => setIsChatOpen(true)}
                  className="px-3 sm:px-4 py-1.5 rounded-full text-xs sm:text-sm font-bold transition-all flex items-center gap-1.5 hover:scale-105 active:scale-95"
                  style={{ backgroundColor: '#22c55e', color: '#ffffff', borderColor: '#16a34a', boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)' }}
                >
                  <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                  </svg>
                  Message
                </button>
                {skinAnalysis?.enabled && (
                  <a
                    href="/skin"
                    className="px-3 sm:px-4 py-1.5 rounded-full text-xs sm:text-sm font-bold transition-all flex items-center gap-1.5 hover:scale-105 active:scale-95"
                    style={{ backgroundColor: skinAccent, color: '#ffffff', boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)' }}
                    title="Understand your skin and get a routine made for it"
                  >
                    <ScanFace className="w-3 h-3 sm:w-4 sm:h-4" />
                    Skin Analysis
                  </a>
                )}
              </div>
            </div>

            <p className="font-medium text-xs sm:text-sm md:text-base leading-relaxed max-w-2xl line-clamp-2" style={{ color: '#64748b' }}>
              {brandData?.tagline || brandData?.description || "Your favorite destination"}
            </p>

            <div className="flex flex-col items-start gap-1.5 sm:gap-2 mt-3 sm:mt-4 text-[9px] sm:text-[10px] md:text-xs font-bold uppercase tracking-widest" style={{ color: '#94a3b8' }}>
              <div className="flex items-center gap-1.5">
                <svg
                  className="w-4 h-4 text-indigo-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                {brandData?.location || "Kampala, Uganda"}
              </div>
              {brandData?.shopNumber && (
                <div className="flex items-center gap-1.5">
                  <svg className="w-4 h-4 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9l1-5h16l1 5M4 9v11a1 1 0 001 1h14a1 1 0 001-1V9M4 9h16M9 21v-6h6v6" />
                  </svg>
                  Shop {brandData.shopNumber}
                </div>
              )}

              {/* Social links — official logos; only the ones that are set, each opens in a new tab */}
              {(() => {
                const s = brandData?.socialLinks || {};
                const waHref = s.whatsapp
                  ? (String(s.whatsapp).startsWith("http") ? s.whatsapp : `https://wa.me/${String(s.whatsapp).replace(/[^0-9]/g, "")}`)
                  : undefined;
                const items = [
                  { key: "instagram", href: s.instagram, label: "Instagram", color: "text-pink-600", d: "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" },
                  { key: "twitter", href: s.twitter, label: "X", color: "text-black", d: "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" },
                  { key: "tiktok", href: s.tiktok, label: "TikTok", color: "text-black", d: "M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" },
                  { key: "youtube", href: s.youtube, label: "YouTube", color: "text-red-600", d: "M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" },
                  { key: "facebook", href: s.facebook, label: "Facebook", color: "text-blue-600", d: "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" },
                  { key: "whatsapp", href: waHref, label: "WhatsApp", color: "text-green-500", d: "M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" },
                  { key: "website", href: s.website, label: "Website", color: "text-emerald-600", d: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" },
                ].filter((i) => !!i.href);
                if (items.length === 0) return null;
                return (
                  <div className="w-full flex items-center flex-wrap gap-2 mt-1">
                    {items.map((i) => (
                      <a
                        key={i.key}
                        href={i.href as string}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={i.label}
                        aria-label={i.label}
                        className={`w-8 h-8 rounded-full flex items-center justify-center bg-white/90 backdrop-blur border border-slate-200 ${i.color} shadow-sm transition-all duration-200 hover:scale-110 hover:shadow-md`}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d={i.d} /></svg>
                      </a>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Brand Statistics Bar — real figures only (Sep 2026): the old bar
            showed the product count as "Collections", a "Monthly Views" that
            was never counted, and "4.9/5" on every shop. */}
        {(() => {
          const stats = [
            {
              label: "Followers",
              value: followerCount ?? brandData?.stats?.followers ?? "0",
              color: "#2563eb",
            },
            {
              label: "Products",
              value: productsData?.pagination?.total?.toString() || brandData?.stats?.totalProducts || productCount || "0",
              color: "#059669",
            },
            ...(collectionCount > 0 ? [{ label: "Collections", value: String(collectionCount), color: "#ea580c" }] : []),
            ...(rating && rating.count > 0
              ? [{ label: rating.count === 1 ? "Rating · 1 review" : `Rating · ${rating.count} reviews`, value: `${rating.average.toFixed(1)}/5`, color: "#9333ea" }]
              : []),
          ];
          return (
        <div className={`grid grid-cols-2 ${STAT_COLS[stats.length] ?? "md:grid-cols-4"} gap-2 sm:gap-3 mb-6 sm:mb-8`}>
          {stats.map((stat, i) => (
            <div
              key={i}
              className="p-3 sm:p-4 rounded-xl flex flex-col items-center justify-center transition-all"
              style={{
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
              }}
            >
              <span
                className="text-[8px] sm:text-[10px] font-black uppercase tracking-widest mb-0.5"
                style={{ color: stat.color }}
              >
                {stat.label}
              </span>
              <span className="text-lg sm:text-xl font-black" style={{ color: '#0f172a' }}>{stat.value}</span>
            </div>
          ))}
        </div>
          );
        })()}

        {/* Content Tabs Navigation */}
        <div className="mb-8 sm:mb-12">
        <div
          ref={tabsRef}
          className="flex items-center gap-1 sm:gap-2 p-1.5 sm:p-2 rounded-lg overflow-x-auto scrollbar-hide"
          style={{ backgroundColor: '#f1f5f9' }}
        >
          {([
            // A hotel leads with its rooms; the shop (minibar, souvenirs) follows.
            ...(hasRooms ? (["Rooms"] as TabType[]) : []),
            // Services before Products when the owner chose it (Brand Settings → Storefront).
            ...(hasServices && servicesFirst ? (["Services"] as TabType[]) : []),
            "Products",
            // The shop's own collections (Sep 2026), when the website shows any today.
            ...(collectionCount > 0 ? (["Collections"] as TabType[]) : []),
            ...(hasServices && !servicesFirst ? (["Services"] as TabType[]) : []),
            // Only offered once the brand has actually published one. Every
            // shop used to carry a Stories tab that opened on "no stories yet".
            ...((storiesData?.length ?? 0) > 0 ? (["Stories"] as TabType[]) : []),
            "Reviews",
          ] as TabType[]).map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                onTabChange?.(tab);
                // Scroll to the matching section smoothly
                if (tab === "Products" || tab === "Services" || tab === "Rooms" || tab === "Collections") {
                  const section = document.getElementById(
                    tab === "Products" ? "products" : tab === "Rooms" ? "rooms" : tab === "Collections" ? "collections" : "services",
                  );
                  if (section) {
                    section.scrollIntoView({ behavior: "smooth", block: "start" });
                  }
                }
              }}
              className={`flex-1 py-2 sm:py-3 px-2 text-xs sm:text-sm md:text-base lg:text-lg font-bold uppercase tracking-wide rounded-md transition-all whitespace-nowrap ${activeTab === tab
                ? ""
                : ""
                }`}
              style={{
                backgroundColor: activeTab === tab ? '#ffffff' : 'transparent',
                color: activeTab === tab ? '#0f172a' : '#64748b',
                boxShadow: activeTab === tab ? '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)' : 'none',
              }}
            >
              {tab}
            </button>
          ))}
          {/* Games Tab - Open modal instead of navigating; only when there's a game */}
          {hasGames && (
            <button
              onClick={() => {
                setIsGamesModalOpen(true);
                setActiveTab("Games");
              }}
              className="flex-1 py-2 sm:py-3 px-2 text-xs sm:text-sm md:text-base lg:text-lg font-bold uppercase tracking-wide rounded-md transition-all text-center whitespace-nowrap"
              style={{ backgroundColor: 'transparent', color: '#64748b' }}
            >
              Games
            </button>
          )}
        </div>
        {/* Where the tabs are scrolled to: a short line, not the browser's bar. */}
        {tabsScroll.overflow && (
          <div className="mx-auto mt-2 h-1 w-12 rounded-full" style={{ backgroundColor: '#e2e8f0' }} aria-hidden>
            <div
              className="h-1 rounded-full"
              style={{
                backgroundColor: '#94a3b8',
                width: `${Math.max(25, Math.round(tabsScroll.shown * 100))}%`,
                marginLeft: `${Math.round(tabsScroll.progress * (100 - Math.max(25, Math.round(tabsScroll.shown * 100))))}%`,
              }}
            />
          </div>
        )}
        </div>
      </div>

      {/* Products Preview - Only in Edit Mode */}
      {isEditMode && activeTab === "Products" && (
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8" style={{ backgroundColor: '#ffffff' }}>
          <h3 className="text-lg sm:text-xl font-bold mb-4" style={{ color: '#0f172a' }}>Products Preview</h3>
          {productsData === undefined ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="space-y-3">
                  <div className="aspect-[3/4] bg-slate-200 rounded-lg animate-pulse" />
                  <div className="h-4 bg-slate-200 rounded animate-pulse" />
                  <div className="h-4 bg-slate-200 rounded w-2/3 animate-pulse" />
                </div>
              ))}
            </div>
          ) : productsData && productsData.products && productsData.products.length > 0 ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {productsData.products.slice(0, 4).map((product: any) => (
                <div key={product._id} className="group" style={{ backgroundColor: '#ffffff' }}>
                  <div className="aspect-[3/4] bg-slate-100 rounded-lg overflow-hidden mb-3 relative">
                    {product.images && product.images[0] ? (
                      <Image
                        src={product.images[0]}
                        alt={product.name}
                        fill
                        className="object-cover group-hover:scale-105 transition-transform duration-300"
                        unoptimized
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-slate-200">
                        <svg className="w-12 h-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <h4 className="text-sm font-semibold line-clamp-2 mb-1" style={{ color: '#0f172a' }}>
                    {product.name}
                  </h4>
                  <p className="text-sm font-bold" style={{ color: '#0f172a' }}>
                    UGX {product.price?.toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12" style={{ backgroundColor: '#ffffff' }}>
              <svg className="w-16 h-16 mx-auto mb-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              <p className="text-base font-medium" style={{ color: '#0f172a' }}>No products yet</p>
              <p className="text-sm mt-1" style={{ color: '#64748b' }}>Add products to see them here</p>
            </div>
          )}
        </div>
      )}

      {/* Tab Content Sections - Only render Stories and Reviews here, Games and Products are handled by BrandShopWrapper */}
      {(activeTab === "Stories" || activeTab === "Reviews") && (
        <div
          className="w-full max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12"
          style={{
            backgroundColor: '#ffffff',
            color: '#0f172a',
            colorScheme: 'light',
          }}
        >
          {/* Stories Tab Content */}
          {activeTab === "Stories" && (
            <div>
              {storiesData === undefined ? (
                // Loading state — tall tiles, the shape most stories are.
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="aspect-[9/16] bg-slate-200 rounded-2xl animate-pulse" />
                  ))}
                </div>
              ) : storiesData && storiesData.length > 0 ? (
                // Stories grid — tall tiles for phone videos, wide cards when
                // the brand's stories are mostly YouTube links.
                storyFrameFor(storiesData) === "portrait" ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                    {storiesData.map((story: any) => (
                      <StoryCard
                        key={story._id}
                        frame="portrait"
                        story={toStoryView(story, { fallbackAvatar: brandData?.profileImage })}
                        onClick={handleStoryClick}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {storiesData.map((story: any) => (
                      <StoryCard
                        key={story._id}
                        story={toStoryView(story, { fallbackAvatar: brandData?.profileImage })}
                        onClick={handleStoryClick}
                      />
                    ))}
                  </div>
                )
              ) : (
                // Empty state
                <div
                  className="flex flex-col items-center justify-center py-12 sm:py-20 text-center"
                  style={{
                    backgroundColor: '#ffffff',
                    color: '#0f172a',
                  }}
                >
                  <div className="w-12 h-12 sm:w-16 sm:h-16 mb-4 sm:mb-6 rounded-full flex items-center justify-center" style={{ backgroundColor: '#f1f5f9' }}>
                    <svg
                      className="w-6 h-6 sm:w-8 sm:h-8"
                      style={{ color: '#94a3b8' }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold mb-2" style={{ color: '#0f172a' }}>No stories yet</h3>
                  <p className="text-base sm:text-lg" style={{ color: '#64748b' }}>
                    {brandData?.name || "This brand"} hasn't posted any stories yet.
                  </p>
                  <p className="text-xs sm:text-sm mt-2 max-w-md px-4" style={{ color: '#94a3b8' }}>
                    Check back soon for exciting content!
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Reviews Tab Content */}
          {activeTab === "Reviews" && brandId && (
            <ReviewsTabContent brandId={brandId} productId={null} />
          )}
        </div>
      )}

      {/* Video Player Modal. overscroll-contain: scrolling the story never
          drags the shop behind it along. */}
      {isModalOpen && selectedStory && (
        <div
          className="fixed inset-0 bg-black/90 z-[9999] flex items-start justify-center overflow-y-auto overscroll-contain"
          onClick={(e) => {
            // Only close if clicking the backdrop itself
            if (e.target === e.currentTarget) {
              handleCloseModal();
            }
          }}
        >
          <div
            className="relative w-full min-h-screen bg-white lg:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button - Positioned outside video area on mobile */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCloseModal();
              }}
              // It sits on the pinned video on a phone: dark glass, not a red disc.
              className="fixed top-2 right-2 z-[10000] w-10 h-10 md:w-12 md:h-12 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur-sm ring-1 ring-white/25 text-white flex items-center justify-center transition-colors shadow-lg"
              aria-label="Close"
            >
              <X className="w-6 h-6 md:w-7 md:h-7" />
            </button>

            {/* Video Player View */}
            <VideoPlayerView
              story={toStoryView(selectedStory, {
                comments: commentsData,
                fallbackAvatar: brandData?.profileImage,
              })}
              relatedStories={storiesData
                ?.filter((s: any) => s._id !== selectedStory._id)
                .slice(0, 5)
                .map((story: any) => toStoryView(story, { fallbackAvatar: brandData?.profileImage })) || []
              }
              onStoryClick={handleStoryClick}
              onAddComment={handleAddComment}
              brandId={brandId as any}
            />
          </div>
        </div>
      )}

      {/* Games Modal */}
      {isGamesModalOpen && (
        <div
          className="fixed inset-0 bg-black/90 z-[9999] flex items-start justify-center overflow-y-auto"
          onClick={(e) => {
            // Only close if clicking the backdrop itself
            if (e.target === e.currentTarget) {
              setIsGamesModalOpen(false);
              setSelectedGame(null);
              // Remove game parameter from URL
              router.push(pathname, { scroll: false });
            }
          }}
        >
          <div
            className="relative w-full min-h-screen p-2 md:p-8"
            style={{ backgroundColor: '#F8FAFC' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsGamesModalOpen(false);
                setSelectedGame(null);
                // Remove game parameter from URL
                router.push(pathname, { scroll: false });
              }}
              className="fixed top-2 right-2 z-[10000] w-10 h-10 md:w-12 md:h-12 rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center transition-colors shadow-lg"
              aria-label="Close"
            >
              <X className="w-6 h-6 md:w-7 md:h-7" />
            </button>

            {/* Games Content - Will be created next */}
            <div className="max-w-7xl mx-auto">
              <GamesTabContent
                brandId={brandId || ""}
                domain={domain || ""}
              />
            </div>
          </div>
        </div>
      )}
      {/* WhatsApp Chat Dialog */}
      {brandId && (
        <TrackingChatDialog
          isOpen={isChatOpen}
          onClose={() => setIsChatOpen(false)}
          brandId={brandId}
          brandName={brandData?.name || "Brand"}
        />
      )}
    </div>
  );
}
